import React, { useState, useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../../../components/Button/Button';
import Modal from '../../../../components/Modal/Modal';
import Notification from '../../../../components/Notification/Notification';
import { CREATE_PASSWORD_RESET_LINK } from '../../../../graphql/mutations';
import Loading from '../../../../components/Loading/Loading';
import './PasswordResetModal.css';

const PasswordResetModal = ({ isOpen, onClose, mateId }) => {
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [hasCreatedLink, setHasCreatedLink] = useState(false);

  const [createPasswordResetLink, { loading }] = useMutation(CREATE_PASSWORD_RESET_LINK, {
    onCompleted: (data) => {
      const resetLink = data.createPasswordResetLink;
      const resetUrl = `${window.location.origin}/reset-password/${resetLink.token}`;
      setLink({ ...resetLink, resetUrl });
      setError(null);
    },
    onError: (error) => {
      setError(error.message);
      setHasCreatedLink(false); // Tillad at prøve igen ved fejl
      setNotification({
        open: true,
        message: 'Fejl ved oprettelse af password reset link: ' + error.message,
        type: 'error'
      });
    }
  });

  const handleCreateLink = useCallback(() => {
    if (!mateId) {
      setError('Ingen bruger valgt');
      return;
    }
    setLink(null);
    setError(null);
    setHasCreatedLink(true);
    createPasswordResetLink({ variables: { mateId } });
  }, [mateId, createPasswordResetLink]);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setNotification({
        open: true,
        message: 'Kopieret til udklipsholder!',
        type: 'success'
      });
    }).catch(() => {
      setNotification({
        open: true,
        message: 'Kunne ikke kopiere til udklipsholder',
        type: 'error'
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setLink(null);
    setError(null);
    setHasCreatedLink(false);
    onClose();
  }, [onClose]);

  // Opret link automatisk når modalen åbnes
  useEffect(() => {
    if (isOpen && mateId && !hasCreatedLink && !loading) {
      setLink(null);
      setError(null);
      setHasCreatedLink(true);
      createPasswordResetLink({ variables: { mateId } }).catch((err) => {
        console.error('Fejl ved oprettelse af password reset link:', err);
        setHasCreatedLink(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mateId]);

  // Ryd state når modalen lukkes
  useEffect(() => {
    if (!isOpen) {
      setLink(null);
      setError(null);
      setHasCreatedLink(false);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Password Reset Link Oprettet"
      >
        {!mateId && (
          <div style={{ padding: '20px' }}>
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fee2e2', 
              borderRadius: '6px',
              border: '1px solid #fca5a5',
              marginBottom: '16px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
                Ingen bruger valgt. Luk modalen og prøv igen.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                title="Luk"
                onClick={handleClose}
                variant="contained"
                color="primary"
              />
            </div>
          </div>
        )}

        {mateId && loading && !link && !error && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Loading />
          </div>
        )}

        {mateId && error && !link && (
          <div style={{ padding: '20px' }}>
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fee2e2', 
              borderRadius: '6px',
              border: '1px solid #fca5a5',
              marginBottom: '16px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
                {error}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                title="Prøv igen"
                onClick={handleCreateLink}
                variant="contained"
                color="primary"
              />
              <Button
                title="Luk"
                onClick={handleClose}
                variant="outlined"
                color="primary"
              />
            </div>
          </div>
        )}

        {link && (
          <div style={{ padding: '20px' }}>
            <div className="password-reset-text">
              Send dette link til brugeren, så de kan nulstille deres password:
            </div>
            
            <div  style={{ marginBottom: '16px' }}>
              <div className="password-reset-label">
                Reset Link:
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  readOnly
                  value={link.resetUrl}
                  className="password-reset-input"
                />
                <Button
                  title="Kopier"
                  onClick={() => copyToClipboard(link.resetUrl)}
                  variant="outlined"
                  color="confirmation"
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div className="password-reset-label">
                Autogenereret Password:
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  readOnly
                  value={link.newPassword}
                  className="password-reset-input password-reset-password"
                />
                <Button
                  title="Kopier"
                  onClick={() => copyToClipboard(link.newPassword)}
                  variant="outlined"
                  color="confirmation"
                />
              </div>
            </div>

            <div className="password-reset-warning">
              <p>
                <strong>Vigtigt:</strong> Dette password vil blive aktiveret når brugeren klikker på linket. 
                Sørg for at sende både linket og passwordet til brugeren.
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Notification
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        type={notification.type}
      />
    </>
  );
};

export default PasswordResetModal;

